import React, { useState } from "react";
import {
  Box,
  Button,
  Stack,
  Card,
  CardContent,
  Typography,
  Radio,
  FormControlLabel,
  FormControl,
  TextField,
  Avatar,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import Styles from "./ClientFeedbackForm.module.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { showToast } from "../../redux/Reducer/toastReducer";
import { updateProjectResponse } from "../../services/feedbackForm.service";

const ClientFeedbackForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const dataReceived = location.state;

  const [selectedOption, setSelectedOption] = useState({});
  const [remarks, setRemarks] = useState({});
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const [errors, setErrors] = useState({});

  // handle option select
  const handleChange = (e, questionId, option) => {
    const { value } = e.target;
    setSelectedOption({
      ...selectedOption,
      [questionId]: {
        answer: value,
        answerId: option._id, // store optionId
        score: option.score,
        isRemark: option.isRemark,
      },
    });

    // clear error when selected
    setErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[questionId];
      return newErrors;
    });
  };

  // handle remark typing
  const handleRemarkChange = (e, questionId, optionId) => {
    setRemarks({
      ...remarks,
      [`${questionId}_${optionId}`]: e.target.value,
    });
  };

  // handle additional feedback
  const handleFeedbackChange = (e) => {
    setAdditionalFeedback(e.target.value);
  };

  // submit
  const handleSubmit = async () => {
    let newErrors = {};
    let isValid = true;

    // validation: ensure all questions are answered
    dataReceived?.questionInfo.forEach((question) => {
      const selected = selectedOption[question._id];
      if (!selected) {
        newErrors[question._id] = "Please select an option for this question";
        isValid = false;
      }
    });

    if (!isValid) {
      setErrors(newErrors);
      return;
    }

    const feedbackData = {
      questions: dataReceived?.questionInfo.map((question) => {
        const selected = selectedOption[question._id];
        return {
          questionId: question._id,
          questionName: question.questionName,
          answer: selected?.answer || null,
          score: selected?.score || 0,
          remark:
            selected?.isRemark
              ? remarks[`${question._id}_${selected?.answerId}`] || ""
              : null,
        };
      }),
      additionalFeedback,
    };

    try {
      const res = await updateProjectResponse(
        dataReceived.transId,
        feedbackData
      );
      navigate("/FeedbackSubmit", { state: dataReceived });
      dispatch(showToast({ message: res.message, type: "success" }));
    } catch (error) {
      dispatch(showToast({ message: error.data.message, type: "error" }));
    }
  };

  return (
    <Box sx={{ background: "#fafafa", minHeight: "100vh" }}>
      {/* Header */}
      <Box className={Styles.Container}>
        <Typography
          style={{
            color: "white",
            fontSize: "24px",
            fontWeight: 700,
            marginLeft: "3%",
          }}
        >
          WSH
        </Typography>
        <Stack
          direction={"row"}
          sx={{
            justifyContent: "center",
            alignItems: "center",
            mr: "1%",
          }}
        >
          <Box
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
            }}
          >
            <Avatar
              sx={{
                height: "40px",
                background: "white",
                color: "#00675d",
                paddingLeft: "10px",
                paddingRight: "10px",
                marginRight: "-5px",
                zIndex: 1,
              }}
            >
              {dataReceived?.contactName?.charAt(0)}
            </Avatar>
            <Box
              sx={{
                background: "#FFFFFF33",
                borderRadius: 5,
                height: "40px",
                width: "200px",
                maxWidth: "150px",
                color: "white",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                textAlign: "center",
                marginLeft: "-13%",
                zIndex: 2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              <Typography style={{ paddingLeft: "7%", color: "white" }}>
                {dataReceived?.contactName?.length > 10
                  ? `${dataReceived?.contactName?.slice(0, 10)}...`
                  : dataReceived?.contactName}
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Box>

      {/* Content */}
      <Stack className={Styles.Content}>
        <Typography className={Styles.Welcome}>Client Feedback Form</Typography>
        <Typography className={Styles.CompanyTitle}>
          Share your thoughts on Workforce Solutions for{" "}
          {dataReceived?.projectName}. Your feedback means
          <br />
          the world to us,{" "}
          {dataReceived?.contact?.contactName || dataReceived?.contactName}.
        </Typography>
      </Stack>

      <Stack sx={{ mt: 3 }}>
        {dataReceived?.questionInfo?.map((question) => (
          <Card className={Styles.QuestionCard} key={question._id}>
            <CardContent>
              {/* Question Title */}
              <Typography className={Styles.Questions}>
                {question.questionName}
              </Typography>

              <FormControl component="fieldset" className={Styles.Options}>
                {question.options.map((option) => (
                  <Box key={option._id}>
                    <FormControlLabel
                      value={option.optionsName}
                      control={
                        <Radio
                          sx={{
                            color:
                              selectedOption[question._id]?.answer ===
                              option.optionsName
                                ? "#00675D"
                                : "default",
                            "&.Mui-checked": {
                              color: "#00675D",
                            },
                          }}
                          checked={
                            selectedOption[question._id]?.answer ===
                            option.optionsName
                          }
                          onChange={(e) =>
                            handleChange(e, question._id, option)
                          }
                        />
                      }
                      label={option.optionsName}
                    />

                    {/* show remark input (optional, per option) */}
                    {option.isRemark &&
                      selectedOption[question._id]?.answer ===
                        option.optionsName && (
                        <Box
                          sx={{
                            mt: 2,
                            p: 2,
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            background: "#fafafa",
                          }}
                        >
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 1, color: "#00675D" }}
                          >
                            Please help us understand your choice
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ mb: 1, color: "text.secondary" }}
                          >
                            You selected <strong>{option.optionsName}</strong>{" "}
                            for the question:{" "}
                            <strong>{question.questionName}</strong>. Could you
                            tell us what the issue is and why?
                          </Typography>
                          <TextField
                            multiline
                            rows={3}
                            fullWidth
                            variant="outlined"
                            placeholder="Write your reason here (optional)..."
                            value={
                              remarks[`${question._id}_${option._id}`] || ""
                            }
                            onChange={(e) =>
                              handleRemarkChange(e, question._id, option._id)
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#00675D",
                                  },
                              },
                            }}
                          />
                        </Box>
                      )}
                  </Box>
                ))}
              </FormControl>

              {errors[question._id] && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {errors[question._id]}
                </Typography>
              )}
            </CardContent>
          </Card>
        ))}

        {dataReceived?.isRemember === true && (
          <Card className={Styles.QuestionCard}>
            <CardContent>
              <Typography variant="h6" sx={{ color: "text.primary" }}>
                {dataReceived.reminderText}{" "}
                <span style={{ color: "red" }}>*</span>
              </Typography>
              <TextField
                multiline
                rows={4}
                variant="outlined"
                fullWidth
                placeholder="Write here..."
                value={additionalFeedback}
                onChange={handleFeedbackChange}
                sx={{
                  marginTop: "10px",
                  borderColor: "#C3C3C3",
                  "& .MuiOutlinedInput-root": {
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#C3C3C3",
                    },
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#C3C3C3",
                  },
                  "& .MuiOutlinedInput-input": {
                    padding: "16px",
                  },
                }}
              />
            </CardContent>
          </Card>
        )}

        <Stack direction="row" spacing={2} className={Styles.buttonContent}>
          <Button className={Styles.SubmitButton} onClick={handleSubmit}>
            Submit
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default ClientFeedbackForm;
